import { useAuthActions } from "@convex-dev/auth/react";
import { useConvexAuth } from "convex/react";
import { useNavigate } from "react-router";
import { useState, useEffect } from "react";
import { Eye, EyeOff, Apple, AlertCircle } from "lucide-react";
import { getUserFriendlyError } from "@/lib/errors";

export default function SignIn() {
  const { signIn } = useAuthActions();
  const { isAuthenticated, isLoading: authLoading } = useConvexAuth();
  const [flow, setFlow] = useState<"signIn" | "signUp">("signIn");
  const [error, setError] = useState<{ message: string; hint?: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [appleLoading, setAppleLoading] = useState(false);
  const navigate = useNavigate();

  const handleAppleSignIn = async () => {
    setAppleLoading(true);
    setError(null);
    try {
      await signIn("apple");
    } catch (err) {
      setError(getUserFriendlyError(err));
      setAppleLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/");
    }
  }, [isAuthenticated, navigate]);

  if (authLoading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center overflow-hidden">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-slate-300 border-t-slate-600" />
      </div>
    );
  }

  return (
    <div className="fixed inset-0 flex items-center justify-center overflow-hidden">
      <div className="w-full max-w-md px-4">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-slate-900 dark:text-slate-100">
            FlexTrack
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-2">
            Track your Flex work blocks
          </p>
        </div>

      <form
        className="flex flex-col gap-4 w-full"
        onSubmit={async (e) => {
          e.preventDefault();
          setLoading(true);
          setError(null);
          const formData = new FormData(e.target as HTMLFormElement);
          formData.set("flow", flow);
          try {
            await signIn("password", formData);
          } catch (err) {
            setError(getUserFriendlyError(err));
            setLoading(false);
          }
        }}
      >
        <button
          type="button"
          onClick={handleAppleSignIn}
          disabled={appleLoading}
          className="bg-black hover:bg-black/90 text-white font-semibold rounded-lg py-3 px-4 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 w-full"
          aria-label="Sign in with Apple"
        >
          <Apple className="h-5 w-5" />
          {appleLoading ? "Loading..." : "Continue with Apple"}
        </button>

        <div className="flex items-center gap-3 my-2">
          <div className="flex-1 h-px bg-slate-300 dark:bg-slate-600" />
          <span className="text-sm text-slate-500 dark:text-slate-400 font-medium">
            or
          </span>
          <div className="flex-1 h-px bg-slate-300 dark:bg-slate-600" />
        </div>

        <input
          className="bg-white dark:bg-slate-800 text-foreground rounded-lg p-3 border border-slate-300 dark:border-slate-600 focus:border-blue-500 dark:focus:border-blue-400 focus:ring-2 focus:ring-blue-200 dark:focus:ring-blue-800 outline-none transition-all"
          type="email"
          name="email"
          placeholder="Email"
          required
        />
        <div className="flex flex-col gap-1">
          <div className="relative">
            <input
              className="bg-white dark:bg-slate-800 text-foreground rounded-lg p-3 pr-10 border border-slate-300 dark:border-slate-600 focus:border-blue-500 dark:focus:border-blue-400 focus:ring-2 focus:ring-blue-200 dark:focus:ring-blue-800 outline-none transition-all w-full"
              type={showPassword ? "text" : "password"}
              name="password"
              placeholder="Password"
              minLength={8}
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 transition-colors"
              tabIndex={-1}
            >
              {showPassword ? (
                <EyeOff className="h-5 w-5" />
              ) : (
                <Eye className="h-5 w-5" />
              )}
            </button>
          </div>
          {flow === "signUp" && (
            <p className="text-xs text-slate-500 dark:text-slate-400 px-1">
              Password must be at least 8 characters
            </p>
          )}
        </div>
        <button
          className="bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white font-semibold rounded-lg py-3 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          type="submit"
          disabled={loading}
        >
          {loading ? "Loading..." : flow === "signIn" ? "Sign in" : "Sign up"}
        </button>
        <div className="flex flex-row gap-2 text-sm justify-center">
          <span className="text-slate-600 dark:text-slate-400">
            {flow === "signIn"
              ? "Don't have an account?"
              : "Already have an account?"}
          </span>
          <button
            type="button"
            className="text-blue-600 dark:text-blue-400 hover:underline font-medium"
            onClick={() => setFlow(flow === "signIn" ? "signUp" : "signIn")}
          >
            {flow === "signIn" ? "Sign up" : "Sign in"}
          </button>
        </div>
        {error && (
          <div className="flex items-start gap-3 rounded-lg bg-amber-50/80 dark:bg-amber-900/20 border border-amber-200/60 dark:border-amber-700/30 p-3">
            <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-500 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-slate-800 dark:text-slate-200">
                {error.message}
              </p>
              {error.hint && (
                <p className="text-xs text-slate-600 dark:text-slate-400 mt-0.5">
                  {error.hint}
                </p>
              )}
            </div>
          </div>
        )}
      </form>
      </div>
    </div>
  );
}
